import config from 'config';
import uuid from 'node-uuid';
import { appLogger } from 'app-lib/helpers/logging';

require('event-source-polyfill');

const LDClient = require('ldclient-js');
let launchDarklyClient;
const logger = appLogger.create('Launch Darkly');

// CONSTANTS
const READY = 'ready';
const CHANGE = 'change';

/**
 * Initializes a connection with LDClient and registers listeners for when the client is ready.
 * @param {Object} user : User object. An object that uniquely identifies a user with whom a connection is initialized with LDClient.
 *  If no user object is provided, an anonymous user with a randomly generated Id is created.
 * @param {Array} onReadyListeners : Array of listeners that need to be notified when connection with Launch Darkly has been initialized.
 */
export function init(user, onReadyListeners) {
    let userObj = user;
    if (!userObj) {
        userObj = {key: uuid.v4(), anonymous: true};
    }
    launchDarklyClient = LDClient.initialize(config.launchDarkly.clientId, userObj);
    launchDarklyClient.on(READY, () => {
        Array.isArray(onReadyListeners) && onReadyListeners.forEach((onReadyFunc) => {
            onReadyFunc();
        });
    });
}

/**
 * Subscribes listeners for changes on feature flag in Launch Darkly. Should only be called if a connection with LDClient has been initialized.
 * @param {String} featureFlag : Name of the feature flag. Mandatory field.
 * @param {Array} listeners : Array of listeners that need to be notified when there is a change in value of
 *  the feature flag on Launch Darkly. Mandatory field.
 */
export function subscribeForFeatureFlagChange(featureFlag, listeners) {
    if (!launchDarklyClient) {
        logger.error('Cannot subscribe for feature flag changes without initializing Launch Darkly Client. Call init first.');
        return;
    }
    if (!featureFlag) {
        logger.error('Cannot subscribe for feature flag changes without a feature flag name.');
        return;
    }
    if (!Array.isArray(listeners)) {
        logger.error('Cannot subscribe for feature flag changes without listeners of type Array.');
        return;
    }
    launchDarklyClient.on(`${CHANGE}:${featureFlag}`, (value, previous) => {
        listeners.forEach((listener) => {
            listener(value, previous);
        });
    });
}

/**
 * Subscribes listeners for changes on all feature flags in Launch Darkly. Should only be called if a connection with LDClient has been initialized.
 * @param {Array} listeners : Array of listeners that need to be notified when there is a change in value of
 *  any feature flags on Launch Darkly. Mandatory field.
 */
export function subscribeForAllFeatureFlagChanges(listeners) {
    if (!launchDarklyClient) {
        logger.error('Cannot subscribe for all feature flag changes without initializing Launch Darkly Client. Call init first.');
        return;
    }
    if (!Array.isArray(listeners)) {
        logger.error('Cannot subscribe for all feature flag changes without listeners of type Array.');
        return;
    }
    launchDarklyClient.on(CHANGE, (settings) => {
        listeners.forEach((listener) => {
            listener(settings);
        });
    });
}

/**
 * Gets the value of a variation/feature glad from Launch Darkly. Should only be called if a connection with LDClient has been initialized.
 * @param {String} featureFlag - Name of feature flag. Mandatory field.
 * @param {*} defaultValue - Default value of feature flag. This value is returned if
 *  a value for the field cannot be obtained for the feature flag. Mandatory field.
 */
export function getVariation(featureFlag, defaultValue) {
    if (!launchDarklyClient) {
        logger.error('Cannot get variation without initializing Launch Darkly Client. Call init first.');
        return null;
    }
    if (!featureFlag) {
        logger.error('Cannot get variation without a feature flag.');
        return null;
    }
    if (defaultValue === undefined || defaultValue === null) {
        logger.error('Cannot get variation without a default value.');
        return null;
    }
    return launchDarklyClient.variation(featureFlag, defaultValue);
}
/**
 * Identifies a user with Launch Darkly.  Should only be called if a connection with LDClient has been initialized.
 * @param {Object} user - User object. An object that uniquely identifies a user with whom a connection is initialized with LDClient.
 * @param {*} listeners - Array of listeners that need to be notified when user has been idenitified with Launch Darkly.
 */
export function identify(user, listeners) {
    if (!launchDarklyClient) {
        logger.error('Cannot identify user without initializing Launch Darkly Client. Call init first.');
        return;
    }
    if (!user) {
        logger.error('Cannot identify user without a user object');
        return;
    }
    launchDarklyClient.identify(user, null, () => {
        Array.isArray(listeners) && listeners.forEach((listener) => {
            listener();
        });
    });
}



// WEBPACK FOOTER //
// ./src/app-lib/launch-darkly/launch-darkly.js